// Payout detail — vendor + period + amount summary, action buttons aligned
// to the current status, and a read-only list of bookings inside the period
// window so the operator can sanity-check the aggregator's math before
// approving a transfer.

import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { DataTable, type DataTableColumn } from '@drivecars/admin-ui';
import { DefRow, DetailLayout, DetailSection } from '@drivecars/admin-ui';
import { EntityActivity } from '@/components/EntityActivity';
import { StatusPill } from '@drivecars/admin-ui';
import { RowAction } from '@/components/RowAction';
import {
  approvePayout,
  cancelPayout,
  fetchAdminPayout,
  markPayoutPaid,
  type AdminPayoutBookingRow,
} from '@/lib/admin-api';

const fmtMoney = (v: number, currency = 'EUR') =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: currency || 'EUR',
    maximumFractionDigits: 2,
  }).format(v);

const fmtDate = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString() : '—';

const BOOKING_COLS: DataTableColumn<AdminPayoutBookingRow>[] = [
  {
    key: 'reference',
    h: 'Booking',
    cell: (r) => <span className="font-mono text-[12px]">{r.reference}</span>,
  },
  {
    key: 'car',
    h: 'Car',
    cell: (r) => r.carTitle ?? '—',
  },
  {
    key: 'period',
    h: 'Dates',
    cell: (r) => (
      <span className="text-[11.5px] text-[color:var(--bo-mute)]">
        {new Date(r.dateFrom).toLocaleDateString()} →{' '}
        {new Date(r.dateTo).toLocaleDateString()}
      </span>
    ),
  },
  { key: 'status', h: 'Status', cell: (r) => <StatusPill status={r.status} /> },
  {
    key: 'total',
    h: 'Total',
    right: true,
    cell: (r) => (
      <span className="tabular-nums">{fmtMoney(r.total, r.currency)}</span>
    ),
  },
  {
    key: 'commission',
    h: 'Commission',
    right: true,
    cell: (r) => (
      <span className="tabular-nums text-[color:var(--bo-mute)]">
        {fmtMoney(r.commission, r.currency)}
      </span>
    ),
  },
];

export function PayoutDetail() {
  const params = useParams({ strict: false }) as { id?: string };
  const id = Number(params.id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'payouts', id],
    queryFn: ({ signal }) => fetchAdminPayout(id, signal),
    enabled: Number.isFinite(id),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'payouts'] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'finance'] });
  };

  const approveMut = useMutation({ mutationFn: approvePayout, onSuccess: invalidate });
  const cancelMut = useMutation({
    mutationFn: ({ id: pid, notes }: { id: number; notes?: string }) =>
      cancelPayout(pid, { notes }),
    onSuccess: invalidate,
  });
  const paidMut = useMutation({
    mutationFn: ({ id: pid, notes }: { id: number; notes?: string }) =>
      markPayoutPaid(pid, { notes }),
    onSuccess: invalidate,
  });

  if (!Number.isFinite(id)) {
    return (
      <DetailLayout title="Invalid id" backTo="/payouts" backLabel="All payouts">
        <div className="rounded-xl border border-[#fecaca] bg-[#fff1f2] p-5 text-[12.5px] text-[#9f1239]">
          Payout id must be numeric.
        </div>
      </DetailLayout>
    );
  }

  if (isLoading) {
    return (
      <DetailLayout title="Loading…" backTo="/payouts" backLabel="All payouts">
        <div className="rounded-xl border border-[color:var(--bo-line)] bg-white p-5 text-[12.5px] text-[color:var(--bo-mute)]">
          Loading payout…
        </div>
      </DetailLayout>
    );
  }
  if (error || !data) {
    return (
      <DetailLayout title="Not found" backTo="/payouts" backLabel="All payouts">
        <div className="rounded-xl border border-[#fecaca] bg-[#fff1f2] p-5 text-[12.5px] text-[#9f1239]">
          {error ? (error as Error).message : 'No payout with that id.'}
        </div>
      </DetailLayout>
    );
  }

  const p = data;
  const busy = approveMut.isPending || cancelMut.isPending || paidMut.isPending;
  const totalBookings = p.bookings.length;
  const summedTotal = p.bookings.reduce((sum, b) => sum + b.total, 0);
  const summedCommission = p.bookings.reduce((sum, b) => sum + b.commission, 0);
  const mutationError = approveMut.error || cancelMut.error || paidMut.error;

  return (
    <DetailLayout
      eyebrow="Finance · Payout"
      title={p.reference}
      sub={`${fmtMoney(p.amount, p.currency)} · ${new Date(p.periodFrom).toLocaleDateString()} → ${new Date(p.periodTo).toLocaleDateString()}`}
      backTo="/payouts"
      backLabel="All payouts"
      actions={
        <>
          {p.status === 'requested' ? (
            <>
              <RowAction
                kind="primary"
                busy={busy}
                onClick={() => approveMut.mutate(p.id)}
              >
                Approve
              </RowAction>
              <RowAction
                kind="danger"
                busy={busy}
                onClick={() => {
                  const notes = window.prompt('Cancellation reason (optional):') ?? undefined;
                  cancelMut.mutate({ id: p.id, notes: notes?.trim() || undefined });
                }}
              >
                Cancel
              </RowAction>
            </>
          ) : null}
          {p.status === 'approved' ? (
            <>
              <RowAction
                kind="primary"
                busy={busy}
                onClick={() => {
                  const notes = window.prompt('Bank reference / note (optional):') ?? undefined;
                  paidMut.mutate({ id: p.id, notes: notes?.trim() || undefined });
                }}
              >
                Mark paid
              </RowAction>
              <RowAction
                kind="danger"
                busy={busy}
                onClick={() => {
                  const notes = window.prompt('Cancellation reason (optional):') ?? undefined;
                  cancelMut.mutate({ id: p.id, notes: notes?.trim() || undefined });
                }}
              >
                Cancel
              </RowAction>
            </>
          ) : null}
          <StatusPill status={p.status} />
        </>
      }
    >
      {mutationError ? (
        <div
          role="alert"
          className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Mutation failed: {(mutationError as Error).message}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <DetailSection title="Vendor">
          <DefRow label="Name">{p.vendor.name}</DefRow>
          <DefRow label="Email">{p.vendor.email}</DefRow>
          <button
            type="button"
            onClick={() =>
              void navigate({
                to: '/crew/$id' as never,
                params: { id: String(p.vendor.id) } as never,
              })
            }
            className="mt-3 text-[12px] font-medium text-[color:var(--bo-accent)] hover:underline"
          >
            View vendor →
          </button>
        </DetailSection>

        <DetailSection title="Period">
          <DefRow label="From">
            {new Date(p.periodFrom).toLocaleDateString()}
          </DefRow>
          <DefRow label="To">{new Date(p.periodTo).toLocaleDateString()}</DefRow>
          <DefRow label="Created">{fmtDate(p.createdAt)}</DefRow>
          <DefRow label="Updated">{fmtDate(p.updatedAt)}</DefRow>
          {p.paidAt ? <DefRow label="Paid at">{fmtDate(p.paidAt)}</DefRow> : null}
        </DetailSection>

        <DetailSection title="Amount">
          <DefRow label="Payout">
            <strong className="tabular-nums">
              {fmtMoney(p.amount, p.currency)}
            </strong>
          </DefRow>
          <DefRow label="Bookings in window">
            {totalBookings} ·{' '}
            <span className="tabular-nums text-[color:var(--bo-mute)]">
              {fmtMoney(summedTotal, p.currency)} gross
            </span>
          </DefRow>
          <DefRow label="Of which commission">
            <span className="tabular-nums text-[color:var(--bo-mute)]">
              {fmtMoney(summedCommission, p.currency)}
            </span>
          </DefRow>
          {p.notes ? <DefRow label="Notes">{p.notes}</DefRow> : null}
        </DetailSection>
      </div>

      <DetailSection title={`Bookings in window (${totalBookings})`}>
        <p className="mb-3 text-[11.5px] text-[color:var(--bo-mute)]">
          Reconstructed from period + vendor — confirmed/completed only.
        </p>
        <DataTable
          columns={BOOKING_COLS}
          rows={p.bookings}
          onRowClick={(r) =>
            void navigate({
              to: '/bookings/$reference' as never,
              params: { reference: r.reference } as never,
            })
          }
          emptyLabel="No matching bookings in this window."
        />
      </DetailSection>

      <EntityActivity entityType="payouts" entityId={p.id} title="Audit trail" />
    </DetailLayout>
  );
}
